import { useState, useRef, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import api from '../api/client.js';

/**
 * Server-backed customer typeahead. Type to search (debounced), click to pick.
 * Unlike SearchableSelect (which filters a fully-loaded static list), this hits
 * GET /customers?q= so it scales to thousands of customers and stays tenant-scoped.
 *
 * Props:
 *   value         — selected customer id ('' / null for none)
 *   valueLabel    — selected customer's name (shown as a chip when value is set)
 *   hintValue     — a placeholder name set instead of a real customer (mutually
 *                   exclusive with value) — shown as a marked "not linked" chip
 *   onChange      — (id, name, isHint) => void
 *                     — real pick:  onChange(id, name)
 *                     — placeholder: onChange(null, name, true)
 *                     — cleared:    onChange(null, '')
 *   placeholder   — input hint
 *   suggestRecent — when true, an empty/focused input shows this rep's 5 most
 *                   recently-touched customers (GET /customers?mine=1&sort=recent)
 *                   instead of nothing, before falling back to normal search as
 *                   soon as the first character is typed. Off by default.
 *   allowHint     — set false to hide the "use as placeholder" option, for
 *                   contexts where a real customer_id is required (e.g. the
 *                   voice agent needs one to actually look up account data).
 *                   Default true.
 */
export default function CustomerSearchSelect({ value, valueLabel, hintValue, onChange, placeholder = 'Search customers…', suggestRecent = false, allowHint = true }) {
  const [text, setText] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(text.trim()), 250);
    return () => clearTimeout(t);
  }, [text]);

  useEffect(() => {
    function onMouseDown(e) {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); }
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const showingSuggestions = suggestRecent && debounced.length === 0;
  const results = useQuery({
    queryKey: showingSuggestions ? ['customers', 'mine-recent'] : ['customers', 'search', debounced],
    queryFn: () => {
      const qs = showingSuggestions
        ? 'mine=1&sort=recent&limit=5&include_synthetic=0'
        : `q=${encodeURIComponent(debounced)}&limit=20&include_synthetic=0`;
      return api.get(`/customers?${qs}`).then(r => r.data.data);
    },
    enabled: open && (debounced.length >= 1 || showingSuggestions),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  // Selected state: show a chip with a clear button.
  if (value) {
    return (
      <div className="flex items-center gap-2 rounded border border-navy-200 bg-panel px-2 py-1.5">
        <span className="text-sm text-navy flex-1 truncate">🏢 {valueLabel || `Customer #${value}`}</span>
        <button type="button" className="text-xs text-crimson hover:underline"
                onClick={() => { onChange(null, ''); setText(''); setDebounced(''); }}>
          ✕ Change
        </button>
      </div>
    );
  }

  // Placeholder-name state: marked distinctly from a real linked customer.
  if (hintValue) {
    return (
      <div className="flex items-center gap-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5">
        <span className="text-sm text-amber-900 flex-1 truncate">
          📝 {hintValue} <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800 align-middle">not linked</span>
        </span>
        <button type="button" className="text-xs text-crimson hover:underline"
                onClick={() => { onChange(null, ''); setText(''); setDebounced(''); }}>
          ✕ Change
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <input
        className="input text-sm"
        placeholder={placeholder}
        value={text}
        onChange={e => { setText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {open && (debounced.length >= 1 || showingSuggestions) && (
        <div className="absolute z-20 left-0 right-0 mt-1 max-h-64 overflow-auto card p-1 shadow-panel">
          {showingSuggestions && (results.data?.length ?? 0) > 0 && (
            <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted">Your recent customers</div>
          )}
          {results.isFetching && <div className="px-2 py-1.5 text-xs text-muted">Searching…</div>}
          {!results.isFetching && (results.data?.length ?? 0) === 0 && (
            <div className="px-2 py-1.5 text-xs text-muted">
              {showingSuggestions
                ? 'No recent customers yet — type to search.'
                : `No matches for "${debounced}".`}
            </div>
          )}
          {results.data?.map(c => (
            <button
              type="button"
              key={c.id}
              className="w-full text-left px-2 py-1.5 rounded hover:bg-panel text-sm"
              onClick={() => { onChange(c.id, c.name); setOpen(false); setText(''); setDebounced(''); }}
            >
              <span className="text-navy">{c.name}</span>
              {(c.city || c.account_number) && (
                <span className="text-xs text-muted">
                  {' '}— {[c.account_number, c.city].filter(Boolean).join(' · ')}
                </span>
              )}
            </button>
          ))}
          {allowHint && !showingSuggestions && debounced.length >= 1 && (
            <button
              type="button"
              className="w-full text-left px-2 py-1.5 rounded hover:bg-amber-50 text-sm text-amber-900 border-t border-navy-100 mt-1"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(null, debounced, true);
                setOpen(false); setText(''); setDebounced('');
              }}
            >
              📝 Use "{debounced}" as a placeholder — not linked to a customer record
            </button>
          )}
        </div>
      )}
    </div>
  );
}
